/**
 * Crafter PrimeVue Theme Preset
 * 
 * Custom dark-first theme extending Aura base preset with Crafter brand colors.
 * 
 * ## Overview
 * This theme maps Crafter's established color palette to PrimeVue design tokens,
 * ensuring consistency across all PrimeVue components and the existing UI.
 * 
 * ## Color System
 * - **Primary Brand**: Cyan (#0891b2) - Main brand color for primary actions
 * - **Surfaces**: Gray scale (900-600) - Dark theme backgrounds and cards
 * - **Text**: White primary, gray variants for hierarchy
 * - **Semantic**: Green (success), Red (error), Yellow (warning), Orange (critical), Blue (info)
 * - **Special**: Purple (impersonation), Teal (KB only) - Preserved for specific features
 * 
 * ## Design Tokens Usage
 * Use these tokens in components for theme-aware styling:
 * 
 * @example
 * // Primary brand elements
 * <Button label="Save" /> // Uses primary tokens automatically
 * <div class="bg-[var(--p-primary-color)]">Brand element</div>
 * 
 * @example
 * // Semantic colors
 * <Message severity="success" /> // Uses success tokens
 * <div class="text-[var(--p-danger-color)]">Error text</div>
 * 
 * @example
 * // Surfaces and backgrounds
 * <Card /> // Uses surface tokens automatically
 * <div class="bg-[var(--p-surface-800)]">Card background</div>
 * 
 * @see {@link https://primevue.org/theming/} PrimeVue Theming Documentation
 * @see {@link https://v4.primevue.org/theming/styled/} Styled Mode Documentation
 * @created 2026-03-12
 */

import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Crafter theme preset configuration
 * 
 * Extends Aura with custom brand colors optimized for dark-first design.
 * The theme is built on a dark gray surface palette with cyan primary accents.
 * 
 * @type {import('@primeuix/themes').ThemePreset}
 */
const Crafter = definePreset(Aura, {
  /**
   * Primitive color definitions
   * 
   * Base color scales that semantic tokens reference.
   * These provide full 50-950 shade scales for flexibility.
   */
  primitive: {
    // Cyan - Primary brand color
    cyan: {
      50: '#ecfeff',
      100: '#cffafe',
      200: '#a5f3fc',
      300: '#67e8f9',
      400: '#22d3ee',
      500: '#06b6d4',  // Light cyan
      600: '#0891b2',  // MAIN - Primary brand color
      700: '#0e7490',  // Hover states
      800: '#155e75',
      900: '#164e63',
      950: '#083344'
    },
    // Gray - Surface and neutral colors (dark theme)
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',  // Muted text
      500: '#6b7280',  // Hover borders
      600: '#4b5563',  // Borders, disabled states (dividers are gray-700)
      700: '#374151',  // Inputs, secondary surfaces
      800: '#1f2937',  // Sidebars, cards, modals
      900: '#111827',  // Main app background
      950: '#030712'
    },
    // Green - Success semantic color
    green: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',  // Success text
      500: '#22c55e',  // Success mid
      600: '#16a34a',  // Success main
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
      950: '#052e16'
    },
    // Red - Error/Danger semantic color
    red: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',  // Error text
      500: '#ef4444',  // Error mid
      600: '#dc2626',  // Error main
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
      950: '#450a0a'
    },
    // Yellow - Warning semantic color
    yellow: {
      50: '#fefce8',
      100: '#fef9c3',
      200: '#fef08a',
      300: '#fde047',
      400: '#facc15',  // Warning text
      500: '#eab308',  // Warning mid
      600: '#ca8a04',  // Warning main
      700: '#a16207',
      800: '#854d0e',
      900: '#713f12',
      950: '#422006'
    },
    // Orange - Warning Critical semantic color
    orange: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',  // Critical warning text
      500: '#f59e0b',  // Critical warning mid
      600: '#d97706',  // Critical warning main
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
      950: '#431407'
    },
    // Blue - Info semantic color
    blue: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',  // Info text
      500: '#3b82f6',  // Info mid
      600: '#2563eb',  // Info main
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554'
    },
    // Purple - Special use (impersonation mode, BotAsks nodes)
    purple: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',  // Purple mid
      600: '#9333ea',
      700: '#7e22ce',  // Impersonation mode
      800: '#6b21a8',
      900: '#581c87',
      950: '#3b0764'
    },
    // Teal - Special use (KB nodes only; toasts use their semantic severity)
    teal: {
      50: '#f0fdfa',
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',  // Teal light
      500: '#14b8a6',
      600: '#0d9488',  // Teal main
      700: '#0f766e',
      800: '#115e59',  // Teal dark
      900: '#134e4a',
      950: '#042f2e'
    }
  },
  
  /**
   * Semantic token definitions
   * 
   * Maps primitive colors to component-level tokens.
   * These are what PrimeVue components actually use.
   */
  semantic: {
    /**
     * Primary brand color mapping
     * 
     * Cyan palette for primary actions, buttons, focus states.
     */
    primary: {
      50: '{cyan.50}',
      100: '{cyan.100}',
      200: '{cyan.200}',
      300: '{cyan.300}',
      400: '{cyan.400}',
      500: '{cyan.500}',
      600: '{cyan.600}',   // Main primary color
      700: '{cyan.700}',
      800: '{cyan.800}',
      900: '{cyan.900}',
      950: '{cyan.950}'
    },
    
    /**
     * Success semantic color mapping
     * 
     * Green palette for success states, confirmations.
     */
    success: {
      50: '{green.50}',
      100: '{green.100}',
      200: '{green.200}',
      300: '{green.300}',
      400: '{green.400}',
      500: '{green.500}',
      600: '{green.600}',
      700: '{green.700}',
      800: '{green.800}',
      900: '{green.900}',
      950: '{green.950}'
    },
    
    /**
     * Error/Danger semantic color mapping
     * 
     * Red palette for errors, destructive actions.
     */
    danger: {
      50: '{red.50}',
      100: '{red.100}',
      200: '{red.200}',
      300: '{red.300}',
      400: '{red.400}',
      500: '{red.500}',
      600: '{red.600}',
      700: '{red.700}',
      800: '{red.800}',
      900: '{red.900}',
      950: '{red.950}'
    },
    
    /**
     * Warning semantic color mapping
     * 
     * Yellow palette for warnings, cautions.
     */
    warn: {
      50: '{yellow.50}',
      100: '{yellow.100}',
      200: '{yellow.200}',
      300: '{yellow.300}',
      400: '{yellow.400}',
      500: '{yellow.500}',
      600: '{yellow.600}',
      700: '{yellow.700}',
      800: '{yellow.800}',
      900: '{yellow.900}',
      950: '{yellow.950}'
    },
    
    /**
     * Info semantic color mapping
     * 
     * Blue palette for informational states.
     */
    info: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    },
    
    /**
     * Color scheme configuration
     * 
     * Defines light and dark mode palettes.
     * Crafter is dark-first, so dark mode is the primary configuration.
     */
    colorScheme: {
      /**
       * Light mode configuration
       * 
       * Minimal light mode support for potential future use.
       * Currently, the app is dark-first with no light mode in use.
       */
      light: {
        primary: {
          color: '{cyan.600}',
          inverseColor: '#ffffff',
          hoverColor: '{cyan.700}',
          activeColor: '{cyan.800}'
        },
        highlight: {
          background: '{cyan.600}',
          focusBackground: '{cyan.700}',
          color: '#ffffff',
          focusColor: '#ffffff'
        },
        surface: {
          0: '#ffffff',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}'
        }
      },
      
      /**
       * Dark mode configuration (PRIMARY)
       * 
       * This is the main color scheme for Crafter's dark-first UI.
       * 
       * Key mappings:
       * - Primary: Cyan brand color for actions
       * - Surface: Gray scale (900=darkest backgrounds, 700=inputs, 600=borders)
       * - Text: White primary, gray for secondary/muted
       * - Focus: Cyan with ring-2 pattern
       * - Interactive states: Darkening hover pattern
       */
      dark: {
        /**
         * Primary color states in dark mode
         */
        primary: {
          color: '{cyan.600}',           // Main primary color
          inverseColor: '#ffffff',       // Text on primary backgrounds
          hoverColor: '{cyan.700}',      // Hover state (darker)
          activeColor: '{cyan.800}'      // Active/pressed state
        },
        
        /**
         * Highlight colors for selected/focused states
         */
        highlight: {
          background: '{cyan.600}',              // Selected item background
          focusBackground: '{cyan.700}',         // Focused item background
          color: '#ffffff',                      // Text on highlighted backgrounds
          focusColor: '#ffffff'                  // Text on focused backgrounds
        },
        
        /**
         * Success color states in dark mode
         */
        success: {
          color: '{green.500}',          // Main success color
          inverseColor: '#ffffff',       // Text on success backgrounds
          hoverColor: '{green.600}',     // Hover state
          activeColor: '{green.700}'     // Active state
        },
        
        /**
         * Error/Danger color states in dark mode
         */
        danger: {
          color: '{red.500}',            // Main error color
          inverseColor: '#ffffff',       // Text on error backgrounds
          hoverColor: '{red.600}',       // Hover state
          activeColor: '{red.700}'       // Active state
        },
        
        /**
         * Warning color states in dark mode
         */
        warn: {
          color: '{yellow.500}',         // Main warning color
          inverseColor: '#000000',       // Text on warning backgrounds (black for yellow)
          hoverColor: '{yellow.600}',    // Hover state
          activeColor: '{yellow.700}'    // Active state
        },
        
        /**
         * Info color states in dark mode
         */
        info: {
          color: '{blue.500}',           // Main info color
          inverseColor: '#ffffff',       // Text on info backgrounds
          hoverColor: '{blue.600}',      // Hover state
          activeColor: '{blue.700}'      // Active state
        },
        
        /**
         * Surface color palette for dark mode
         * 
         * Maps to gray scale based on usage:
         * - 0: White (for contrast elements)
         * - 50-300: Light grays (for text hierarchy)
         * - 400: Muted text (gray.400)
         * - 500: Hover borders
         * - 600: Borders, disabled states (dividers are gray-700, see the divider block)
         * - 700: Inputs, secondary surfaces (200+ uses)
         * - 800: Sidebars, cards, modals (150+ uses)
         * - 900: Main app background (50+ uses)
         * - 950: Darkest backgrounds
         */
        surface: {
          0: '#ffffff',            // White for contrast
          50: '{gray.50}',         // Lightest gray
          100: '{gray.100}',       // Very light gray
          200: '{gray.200}',       // Light gray
          300: '{gray.300}',       // Tertiary text (50+ uses)
          400: '{gray.400}',       // Muted/secondary text (100+ uses)
          500: '{gray.500}',       // Hover borders
          600: '{gray.600}',       // Borders, disabled (50+ uses; dividers are gray-700)
          700: '{gray.700}',       // Inputs, secondary surfaces (200+ uses)
          800: '{gray.800}',       // Sidebars, cards, modals (150+ uses)
          900: '{gray.900}',       // Main app background (50+ uses)
          950: '{gray.950}'        // Darkest background
        },
        
        /**
         * Focus ring configuration
         * 
         * Follows the Crafter pattern: focus:ring-2 focus:ring-cyan-500
         */
        focusRing: {
          width: '2px',
          style: 'solid',
          color: '{cyan.500}',
          offset: '2px'
        },

        /**
         * Form field colors (GLOBAL for all form components)
         * 
         * This configuration applies to ALL form components that inherit from formField:
         * - InputText, Textarea, Password
         * - Select, Dropdown, MultiSelect, AutoComplete
         * - InputNumber, Calendar, etc.
         * 
         * Individual components can override these in the components section below.
         */
        formField: {
          background: '{surface.900}',        // Input background
        }
      }
    },
  },
  
  /**
   * Component-specific overrides
   * 
   * Fine-tune individual component styling to match Crafter design patterns.
   */
  components: {
    /**
     * Button component customization
     * 
     * STRUCTURE NOTE (PrimeVue v4 / @primeuix/themes v2.0.3):
     * As in Aura, severity tokens live under colorScheme > dark > root > severity,
     * NOT directly under colorScheme > dark > severity. The variant groups
     * (`outlined`, `text`, `link`) are siblings of `root`, not entries inside it.
     *
     * Correct path: button.colorScheme.dark.root.primary.color
     * Wrong path: button.colorScheme.dark.primary.color (previous mistake)
     * Variant path: button.colorScheme.dark.text.secondary.hoverBackground
     */
    button: {
      colorScheme: {
        dark: {
          root: {
            primary: {
              background: '{cyan.600}',
              hoverBackground: '{cyan.700}',
              activeBackground: '{cyan.800}',
              borderColor: '{cyan.600}',
              hoverBorderColor: '{cyan.700}',
              activeBorderColor: '{cyan.800}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            secondary: {
              background: '{gray.700}',
              hoverBackground: '{gray.600}',
              activeBackground: '{gray.500}',
              borderColor: '{gray.700}',
              hoverBorderColor: '{gray.600}',
              activeBorderColor: '{gray.500}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            danger: {
              background: '{red.700}',
              hoverBackground: '{red.800}',
              activeBackground: '{red.900}',
              borderColor: '{red.700}',
              hoverBorderColor: '{red.800}',
              activeBorderColor: '{red.900}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            warn: {
              background: '{orange.500}',
              hoverBackground: '{orange.600}',
              activeBackground: '{orange.700}',
              borderColor: '{orange.500}',
              hoverBorderColor: '{orange.600}',
              activeBorderColor: '{orange.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            /**
             * `severity="help"`: the reserved purple, only for BotAsks and code-node
             * actions (e.g. "Open code editor"), never a general accent (DESIGN.md
             * Reserved-Hue Rule). Matches the former DIY
             * `bg-purple-600 hover:bg-purple-700 text-white`; Aura's dark default
             * is a light purple.400 fill with purple.950 text. The focus ring is
             * left at Aura's {purple.400}, like danger/warn. Outlined/text help keep
             * the Aura defaults ({purple.400} text), the same tint as the flow node
             * accents.
             */
            help: {
              background: '{purple.600}',
              hoverBackground: '{purple.700}',
              activeBackground: '{purple.800}',
              borderColor: '{purple.600}',
              hoverBorderColor: '{purple.700}',
              activeBorderColor: '{purple.800}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            }
          },
          /**
           * Text secondary variant (`<Button severity="secondary" text>`): hover fill.
           *
           * Aura's dark default is {surface.800} ({gray.800}, #1f2937), the same
           * tone as the cards, panels and Dialogs these buttons sit on. This sets it
           * to {gray.600} (#4b5563). The text colour ({surface.400}) and the active
           * fill ({surface.700}) keep the Aura defaults.
           * In Aura's token tree `text` is a sibling of `root`, like `link` below:
           * button.colorScheme.dark.text.secondary.hoverBackground
           * -> --p-button-text-secondary-hover-background.
           */
          text: {
            secondary: {
              hoverBackground: '{gray.600}'
            }
          },
          /**
           * Link variant (`<Button variant="link">`, e.g. BackButton).
           *
           * Mirrors the former DIY back-link colours (`text-cyan-400`,
           * `hover:text-cyan-300`). Aura sets all three states to {primary.color}
           * (cyan.600 here): darker, with no hover change.
           * In Aura's token tree `link` is a sibling of `root`, not a severity
           * inside it: button.colorScheme.dark.link.color.
           */
          link: {
            color: '{cyan.400}',
            hoverColor: '{cyan.300}',
            activeColor: '{cyan.300}'
          }
        }
      },
      /**
       * Outlined secondary (`<Button severity="secondary" outlined>`): hover border.
       *
       * Aura's dark outlined secondary is transparent with a {surface.700} (gray-700)
       * border, {surface.400} text and only a rgba(255,255,255,0.04) hover overlay,
       * about 1.1:1 against the surface it sits on, so the hover barely shows.
       * @primeuix/themes 2.0.3 has no outlined hover-border token: Aura's hover and
       * active rules re-apply the rest border. This adds one. On hover the border
       * lightens to Border Hover {gray.500}, the same hover edge as the fields
       * (DESIGN.md "Border"): 3.0:1 against gray-800, where the gray-700 rest border
       * is 1.4:1. Background, text colour and the active fill keep the Aura defaults.
       * Not cyan: in the field system a cyan border means focus (Signal Rule).
       *
       * Also used for card-as-button tiles (multi-line clickable cards, e.g. the
       * email variable picker), see DESIGN.md "Buttons".
       *
       * The token is a component-level `extend` token
       * (--p-button-outlined-secondary-hover-border-color), so it exists in both
       * colour schemes. The extra `.p-button` in the selector outranks Aura's
       * outlined-secondary hover and active rules (0,4,0) whatever the injection
       * order, so the light border also holds while the button is pressed.
       */
      extend: {
        outlinedSecondary: {
          hoverBorderColor: '{gray.500}'
        }
      },
      css: ({ dt }) => `
        .p-button.p-button-outlined.p-button-secondary:not(:disabled):hover {
            border-color: ${dt('button.outlined.secondary.hover.border.color')};
        }
      `
    },
    
    /**
     * Input field customization
     */
    inputtext: {
      colorScheme: {
        dark: {
          background: '{gray.700}',
          hoverBackground: '{gray.700}',
          focusBackground: '{gray.700}',
          borderColor: '{gray.600}',
          hoverBorderColor: '{gray.500}',
          focusBorderColor: '{cyan.500}',
          color: '#ffffff',
          placeholderColor: '{gray.400}',
          disabledBackground: '{gray.600}',
          disabledColor: '{gray.500}'
        }
      }
    },

    /**
     * Select customization
     *
     * Mirrors `inputtext` above so InputText, Select and Textarea share one field
     * look (gray-700 surface, gray-600 border, cyan-500 focus border). Without it
     * Select falls back to the generic `formField` tokens ({surface.900}), i.e. a
     * darker field next to an InputText. Field components that still read
     * `formField` directly (MultiSelect, AutoComplete `multiple`, InputGroupAddon,
     * RadioButton) keep the {surface.900} background.
     *
     * Only the field surface is set: the dropdown panel (overlay/list/option)
     * keeps the Aura defaults. `hoverBackground`/`focusBackground` are omitted on
     * purpose — they are not Select/Textarea tokens in @primeuix/themes 2.0.3 (only
     * the `filled*` variants exist), and the background is the same in every state.
     */
    select: {
      colorScheme: {
        dark: {
          background: '{gray.700}',
          borderColor: '{gray.600}',
          hoverBorderColor: '{gray.500}',
          focusBorderColor: '{cyan.500}',
          color: '#ffffff',
          placeholderColor: '{gray.400}',
          disabledBackground: '{gray.600}',
          disabledColor: '{gray.500}'
        }
      }
    },

    /**
     * Textarea customization
     *
     * Same field tokens as `inputtext` and `select` (see the Select note above).
     */
    textarea: {
      colorScheme: {
        dark: {
          background: '{gray.700}',
          borderColor: '{gray.600}',
          hoverBorderColor: '{gray.500}',
          focusBorderColor: '{cyan.500}',
          color: '#ffffff',
          placeholderColor: '{gray.400}',
          disabledBackground: '{gray.600}',
          disabledColor: '{gray.500}'
        }
      }
    },

    /**
     * ToggleSwitch customization
     *
     * Aura's dark off track is {surface.700} = {gray.700} (#374151), the Surface
     * Input tone. On a gray-700 surface (the disabled tool/connector cards in
     * Tools.vue and Connectors.vue, gray-700 toggle rows in the flow panels) an OFF
     * switch loses its track and only a floating knob is left, while a disabled
     * switch ({surface.600} track) reads stronger than an enabled one. The off state
     * moves one step up the graphite scale, the same border / border-hover pair as
     * the fields (DESIGN.md "Border"):
     * - off track {gray.600}, hover {gray.500}: 1.4:1 / 2.1:1 against a gray-700
     *   card (the contrast the old track had on gray-800), 1.9:1 / 3.0:1 against
     *   gray-800;
     * - off handle {gray.300}, hover {gray.200} (Aura: {surface.400} / {surface.300}):
     *   5.1:1 / 3.9:1 on the off track, ~7:1 against a gray-700 card. The knob
     *   carries the off state, the track frames it.
     *
     * Kept from Aura on purpose (no override needed):
     * - on: track {primary.color} = {cyan.600}, hover {primary.hover.color} =
     *   {cyan.700}, handle {surface.900} (4.8:1 / 3.3:1 on the cyan track);
     * - disabled: track {surface.600} = {gray.600}, handle {surface.900}. Same track
     *   as off, but the dark knob (2.3:1) reads dimmer than the light off knob
     *   (5.1:1), so the order stays on > off > disabled and a disabled switch still
     *   shows on a gray-700 card. PrimeVue keeps `opacity: 1` on a disabled switch,
     *   and its `.p-disabled` track rule wins over the checked one: a disabled ON
     *   switch is the same gray track with the dark knob on the right.
     *
     * STRUCTURE NOTE (@primeuix/themes v2.0.3): as in Aura, the colours live under
     * colorScheme > dark > root (track) and colorScheme > dark > handle (knob):
     * toggleswitch.colorScheme.dark.root.background -> --p-toggleswitch-background,
     * toggleswitch.colorScheme.dark.handle.background -> --p-toggleswitch-handle-background.
     * A direct `toggleswitch.root.background` would be ignored: Aura defines these
     * tokens per colour scheme, and colorScheme takes precedence.
     */
    toggleswitch: {
      colorScheme: {
        dark: {
          root: {
            background: '{gray.600}',
            hoverBackground: '{gray.500}'
          },
          handle: {
            background: '{gray.300}',
            hoverBackground: '{gray.200}'
          }
        }
      }
    },

    /**
     * Card component customization
     */
    card: {
      colorScheme: {
        dark: {
          background: '{gray.800}',
          borderColor: '{gray.700}',
          color: '#ffffff'
        }
      }
    },
    
    /**
     * Panel component customization
     */
    panel: {
      colorScheme: {
        dark: {
          background: '{gray.800}',
          borderColor: '{gray.700}',
          color: '#ffffff',
          headerBackground: '{gray.800}',
          headerBorderColor: '{gray.700}',
          headerColor: '#ffffff'
        }
      }
    },

    /**
     * Accordion customization (disclosure sections, e.g. the flow-panel syntax guides)
     *
     * Aura's dark Accordion is a flat list on {content.background} (surface-900)
     * with roomy 1.125rem headers at weight 600 and a bottom rule under each panel.
     * Here it becomes a quiet raised card that sits flush on a gray-800 panel or
     * Dialog (DESIGN.md tonal layering):
     * - header and content on {gray.800}; header text {gray.300} and chevron
     *   {gray.400}, both white on hover and when open; hover lifts the header one
     *   plane to {gray.700}, also while open, so the hover does not vanish on click;
     * - dense padding (header 0.5rem 0.75rem, content 0 0.75rem 0.75rem), weight 500;
     * - a 1px border on every side of each panel. Colour: no override needed, Aura's
     *   `panel.borderColor` is {content.border.color} = {surface.700} = {gray.700}.
     * The focus ring is untouched: it reads the theme's {focus.ring.*} (2px
     * {cyan.500}), inset by Aura's -1px offset so it sits on the card edge.
     *
     * Radii: Aura already rounds the first header's top and the last header's bottom
     * corners ({content.border.radius}, 6px) but has no panel radius token, so the
     * `css` below (more specific than Aura's `.p-accordionpanel`, no `!`) rounds
     * the panel border with the same tokens, drops the top border of each following
     * panel (one 1px rule between stacked panels, not two) and rounds the open last
     * panel's content so its fill does not cover the curved border. A single panel
     * is a rounded card; several panels stack into one card.
     */
    accordion: {
      panel: {
        borderWidth: '1px'
      },
      header: {
        padding: '0.5rem 0.75rem',
        fontWeight: '500'
      },
      content: {
        padding: '0 0.75rem 0.75rem 0.75rem'
      },
      colorScheme: {
        dark: {
          header: {
            color: '{gray.300}',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
            activeHoverColor: '#ffffff',
            background: '{gray.800}',
            hoverBackground: '{gray.700}',
            activeBackground: '{gray.800}',
            activeHoverBackground: '{gray.700}',
            toggleIcon: {
              color: '{gray.400}',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              activeHoverColor: '#ffffff'
            }
          },
          content: {
            background: '{gray.800}'
          }
        }
      },
      css: ({ dt }) => `
        .p-accordionpanel:first-child {
            border-start-start-radius: ${dt('accordion.header.first.top.border.radius')};
            border-start-end-radius: ${dt('accordion.header.first.top.border.radius')};
        }

        .p-accordionpanel:last-child {
            border-end-start-radius: ${dt('accordion.header.last.bottom.border.radius')};
            border-end-end-radius: ${dt('accordion.header.last.bottom.border.radius')};
        }

        .p-accordionpanel + .p-accordionpanel {
            border-top-width: 0;
        }

        .p-accordionpanel:last-child > .p-accordioncontent > .p-accordioncontent-wrapper > .p-accordioncontent-content {
            border-end-start-radius: ${dt('accordion.header.last.bottom.border.radius')};
            border-end-end-radius: ${dt('accordion.header.last.bottom.border.radius')};
        }
      `
    },

    /**
     * DataTable customization (data tables in cards and on the page)
     *
     * Aura's dark DataTable paints its own slab: header cells and rows on
     * {content.background} ({surface.900} = {gray.900}) with white titles, and
     * `datatable.border.color` = {surface.800} ({gray.800}) rules, which vanish on
     * the gray-800 cards most tables sit in. Here the table takes the plane it sits
     * on (DESIGN.md "Tables"), like the former DIY Analytics tables:
     * - header, header cells, rows, footer and footer cells are `transparent`: a
     *   table in a gray-800 card is gray-800, one on the page is gray-900;
     * - header text {gray.400} (#9ca3af: 5.8:1 on gray-800, 7.0:1 on gray-900) at
     *   0.75rem. The title weight is Aura's `columnTitle.fontWeight` 600 (no
     *   override needed);
     * - rules {gray.700} (#374151: 1.4:1 on gray-800, 1.7:1 on gray-900), the same
     *   line as a Divider or a card edge;
     * - row hover (`rowHover`) is {gray.700} at 50%, one subtle graphite step on
     *   either plane (~#2b3544 on gray-800, 1.2:1; ~#242c3c on gray-900, 1.3:1).
     *   Aura's {content.hover.background} is {gray.800}, invisible on a gray-800 card;
     * - selected rows (`selectionMode`, unused today) are {primary.color} (cyan.600)
     *   at 20% with Aura's {highlight.color} white text (11:1 on gray-800), not the
     *   solid cyan.600 highlight (white on it is 3.7:1). Aura's {primary.900}
     *   selected-row rule is kept;
     * - the row expander button (AdminBots) hovers to {gray.600}, like the text
     *   secondary Buttons beside it: Aura's {content.hover.background} ({gray.800})
     *   would vanish on gray-800 rows.
     *
     * Header font size: Aura has no token for it, so it is an `extend` token
     * (--p-datatable-header-cell-font-size) applied by the `css` below, on the
     * header cell so a custom #header slot (DataBrowser) gets it too. The key is
     * `headerCellFont`, not `headerCell`: `extend` is shallow-merged into the
     * component tokens, so `extend.headerCell` would replace Aura's whole
     * `headerCell` group (padding, colours, focus ring).
     *
     * The `css` also repeats Aura's own datatable `css` verbatim (the loading-mask
     * tint): definePreset deep-merges plain objects only, so a preset `css`
     * replaces Aura's string, and `:loading` would then dim the table with the
     * global {mask.background} (rgba(0,0,0,0.6)) instead of 0.3.
     *
     * Kept from Aura (no table uses them): the sortable header states (hover
     * {gray.800}, sorted = the solid cyan.600 highlight), striped rows
     * ({surface.950}), and `scrollable` / frozen columns. A scrollable table with a
     * sticky header would need an opaque header: rows scroll under a transparent one.
     *
     * STRUCTURE NOTE (@primeuix/themes v2.0.3): Aura keeps the background and text
     * colours at the root of the block (datatable.row.background) and only the
     * border, striped and selected-border colours under colorScheme. As in the other
     * blocks, every colour here sits under colorScheme > dark and wins over Aura's
     * root value in the dark scheme:
     * datatable.colorScheme.dark.root.borderColor -> --p-datatable-border-color,
     * datatable.colorScheme.dark.headerCell.color -> --p-datatable-header-cell-color,
     * datatable.colorScheme.dark.row.hoverBackground -> --p-datatable-row-hover-background.
     * The light scheme keeps the Aura defaults (the app is dark-only).
     */
    datatable: {
      extend: {
        headerCellFont: {
          size: '0.75rem'
        }
      },
      colorScheme: {
        dark: {
          root: {
            borderColor: '{gray.700}'
          },
          header: {
            background: 'transparent'
          },
          headerCell: {
            background: 'transparent',
            color: '{gray.400}'
          },
          row: {
            background: 'transparent',
            hoverBackground: 'color-mix(in srgb, {gray.700}, transparent 50%)',
            selectedBackground: 'color-mix(in srgb, {primary.color}, transparent 80%)'
          },
          footerCell: {
            background: 'transparent'
          },
          footer: {
            background: 'transparent'
          },
          rowToggleButton: {
            hoverBackground: '{gray.600}'
          }
        }
      },
      css: ({ dt }) => `
        .p-datatable-mask.p-overlay-mask {
            --px-mask-background: light-dark(rgba(255,255,255,0.5),rgba(0,0,0,0.3));
        }

        .p-datatable-header-cell {
            font-size: ${dt('datatable.header.cell.font.size')};
        }
      `
    },

    /**
     * Paginator customization (the page bar of a paginated DataTable)
     *
     * Every Paginator today is a DataTable's (`paginator` on AdminUsers, AdminBots,
     * AdminWhatsapp and Billing): there is no standalone <Paginator>. Aura's dark
     * Paginator is a {content.background} ({surface.900} = {gray.900}) bar, so under
     * a transparent table on a gray-800 host it showed as a darker strip. It now
     * takes the host plane like the table (see the datatable block above):
     * - root background `transparent`;
     * - nav buttons (first/prev/next/last and the page numbers) hover to {gray.600}
     *   (#4b5563: 1.9:1 on gray-800, 2.3:1 on gray-900), like the text secondary
     *   Buttons and the DataTable row expander. Aura's {content.hover.background}
     *   ({gray.800}) would vanish on a gray-800 host.
     * Kept from Aura (no override needed): nav text {text.muted.color} ({gray.400}),
     * hover text {text.hover.muted.color} ({gray.300}), and the current page on the
     * {highlight.background} fill (cyan.600, white text).
     *
     * STRUCTURE NOTE (@primeuix/themes v2.0.3): Aura's Paginator has no colorScheme,
     * all its tokens sit at the root of the block. As in the other blocks, the
     * colours here sit under colorScheme > dark and win over Aura's root values in
     * the dark scheme:
     * paginator.colorScheme.dark.root.background -> --p-paginator-background,
     * paginator.colorScheme.dark.navButton.hoverBackground -> --p-paginator-nav-button-hover-background.
     * The light scheme keeps the Aura defaults (the app is dark-only).
     */
    paginator: {
      colorScheme: {
        dark: {
          root: {
            background: 'transparent'
          },
          navButton: {
            hoverBackground: '{gray.600}'
          }
        }
      }
    },

    /**
     * Dialog/Modal component customization
     */
    dialog: {
      colorScheme: {
        dark: {
          background: '{gray.800}',
          borderColor: '{gray.700}',
          color: '#ffffff',
          headerBackground: '{gray.800}',
          headerColor: '#ffffff',
          closeBorderColor: 'transparent',
          closeHoverBorderColor: 'transparent',
          closeColor: '{gray.400}',
          closeHoverColor: '#ffffff'
        }
      }
    },
    
    /**
     * Toast/Message component customization
     * 
     * Uses opacity patterns for notification backgrounds:
     * - Success: bg-green-900/20
     * - Error: bg-red-900/20
     * - Warning: bg-yellow-900/50
     * - Info: bg-blue-900/20
     */
    /**
     * Message component customization (banner inline)
     *
     * Aura di default rende i Message con un fill al 16% della tinta .500 e il
     * TESTO nella stessa .500: su grafite l'errore esce sotto 4.5:1, cioè l'avviso
     * meno leggibile della superficie è proprio quello che dice "le tue modifiche
     * non verranno salvate". Qui si allinea il componente al pattern firma di
     * DESIGN.md — fill semantico traslucido, bordo PIENO {hue}.600, testo in tinta
     * più chiara — che è anche quello già usato da `toast`.
     *
     * `simple` (variant senza fill né bordo, usata per gli avvisi compatti dentro
     * una card) tiene il valore del token dichiarato in DESIGN.md, non uno step
     * più chiaro: warn = {yellow.500} = #eab308.
     */
    message: {
      colorScheme: {
        dark: {
          success: {
            background: 'rgba(20, 83, 45, 0.5)',   // green.900 al 50%
            borderColor: '{green.600}',
            color: '{green.300}',
            simple: { color: '{green.500}' }
          },
          error: {
            background: 'rgba(127, 29, 29, 0.5)',  // red.900 al 50%
            borderColor: '{red.600}',
            color: '{red.300}',
            simple: { color: '{red.500}' }
          },
          warn: {
            background: 'rgba(113, 63, 18, 0.5)',  // yellow.900 al 50%
            borderColor: '{yellow.600}',
            color: '{yellow.300}',
            simple: { color: '{yellow.500}' }
          },
          info: {
            background: 'rgba(30, 58, 138, 0.5)',  // blue.900 al 50%
            borderColor: '{blue.600}',
            color: '{blue.300}',
            simple: { color: '{blue.500}' }
          }
        }
      }
    },

    toast: {
      colorScheme: {
        dark: {
          success: {
            background: 'rgba(20, 83, 45, 0.3)',   // green.900 with 30% opacity
            borderColor: '{green.600}',
            color: '#ffffff',
            detailColor: '{gray.300}',
            closeIconColor: '#ffffff',
            closeIconHoverColor: '#ffffff'
          },
          error: {
            background: 'rgba(127, 29, 29, 0.3)',  // red.900 with 30% opacity
            borderColor: '{red.600}',
            color: '#ffffff',
            detailColor: '{gray.300}',
            closeIconColor: '#ffffff',
            closeIconHoverColor: '#ffffff'
          },
          warn: {
            background: 'rgba(113, 63, 18, 0.5)',  // yellow.900 with 50% opacity
            borderColor: '{yellow.600}',
            color: '#ffffff',
            detailColor: '{gray.300}',
            closeIconColor: '#ffffff',
            closeIconHoverColor: '#ffffff'
          },
          info: {
            background: 'rgba(30, 58, 138, 0.3)',  // blue.900 with 30% opacity
            borderColor: '{blue.600}',
            color: '#ffffff',
            detailColor: '{gray.300}',
            closeIconColor: '#ffffff',
            closeIconHoverColor: '#ffffff'
          }
        }
      }
    },

    /**
     * ProgressSpinner customization (loading indicator)
     *
     * Aura's dark spinner cycles its stroke through four hues ({red.400},
     * {blue.400}, {green.400}, {yellow.400}): on graphite that reads as a status
     * signal (error, info, success, warning) rather than "loading", and breaks the
     * Rationed-Color Rule (DESIGN.md). All four stops are set to the same
     * {primary.400} ({cyan.400}, #22d3ee), so every spinner is one cyan stroke:
     * the colour of the former DIY spinner ring (`border-cyan-400`) and the .400
     * step Aura itself uses for dark spinners, which reads against every graphite
     * plane (~8:1 on gray-800). {primary.color} (cyan.600) would be the darker
     * button fill, not a thin stroke on graphite.
     *
     * Sizing is not a token: `.p-progressspinner` hardcodes 100px width/height
     * in unlayered CSS, so Tailwind `w-*`/`h-*` lose. Size per instance with an
     * inline style, e.g. <ProgressSpinner style="width: 2rem; height: 2rem" stroke-width="3" />
     * (viewBox is 50 units wide: 3 units at 2rem = ~2px, the old `border-2` ring).
     *
     * STRUCTURE NOTE (@primeuix/themes v2.0.3): as in Aura, the stops live under
     * colorScheme > dark > root:
     * progressspinner.colorScheme.dark.root.colorOne -> --p-progressspinner-color-one
     * (likewise colorTwo/colorThree/colorFour). The light scheme keeps the Aura
     * defaults (the app is dark-only).
     */
    progressspinner: {
      colorScheme: {
        dark: {
          root: {
            colorOne: '{primary.400}',
            colorTwo: '{primary.400}',
            colorThree: '{primary.400}',
            colorFour: '{primary.400}'
          }
        }
      }
    },

    /**
     * Skeleton customization (loading placeholder bars)
     *
     * Aura's dark Skeleton is a translucent white film (rgba(255,255,255,0.06),
     * wave rgba(255,255,255,0.04)): on a gray-800 Dialog or card it blends to
     * ~#2c3643, 1.2:1, fainter than the DIY `bg-gray-700` placeholders it
     * replaces. The bar becomes the Surface Input plane, solid {gray.700}
     * (#374151): 1.4:1 on gray-800, 1.7:1 on gray-900, the same well as the
     * fields it stands in for. The wave peaks one graphite step up at {gray.600}
     * (#4b5563, the Border tone, 1.4:1 on the bar); the style fades it in and out
     * from transparent (linear-gradient(90deg, transparent, wave, transparent)),
     * so a soft band sweeps across with no hard edge. A white overlay at Aura's
     * 0.04 would be ~1.1:1 on gray-700, barely visible.
     *
     * `borderRadius` keeps the Aura default ({content.border.radius}, 6px, the
     * field radius). Size per instance with the `width`/`height` props (and
     * `border-radius` or `shape="circle"` for pills and avatars): they are inline
     * styles (defaults 100% / 1rem), so Tailwind `w-*`/`h-*` lose. A dimmer hint
     * line takes `class="opacity-60"`, which gives exactly the old
     * `bg-gray-700/60` (PrimeVue sets no opacity on `.p-skeleton`).
     *
     * STRUCTURE NOTE (@primeuix/themes v2.0.3): as in Aura, the colours live under
     * colorScheme > dark > root:
     * skeleton.colorScheme.dark.root.background -> --p-skeleton-background,
     * skeleton.colorScheme.dark.root.animationBackground -> --p-skeleton-animation-background.
     * The light scheme keeps the Aura defaults (the app is dark-only).
     */
    skeleton: {
      colorScheme: {
        dark: {
          root: {
            background: '{gray.700}',
            animationBackground: '{gray.600}'
          }
        }
      }
    },

    /**
     * Chip component customization
     *
     * Overrides the default pill/full border-radius with a more rectangular shape
     * to match Crafter's compact badge style.
     */
    chip: {
      root: {
        borderRadius: '6px',
        paddingX: '0.4rem',
        paddingY: '0.2rem'
      }
    },

    /**
     * Tag customization (status, score and error-type pills)
     *
     * Aura's Tag is 0.875rem (14px) at weight 700, one size and one weight above
     * the former DIY pills (0.75rem / 600) the 16 Tags replaced (connector and
     * WhatsApp statuses, Analytics scores and error types, Transcripts, the admin
     * tables). Here it goes back to the old pill (DESIGN.md "Tags"):
     * - font size 0.75rem (12px, the Label size), weight 600;
     * - padding: no override needed. Aura's 0.25rem 0.5rem (4px 8px) at 12px is
     *   the usual compact badge (`px-2 py-1`), and the pill shrinks with its font.
     *   Tag sets no line-height, so it inherits the host's: ~26px tall at the
     *   body's 1.5 (Aura's 14px Tag: ~29px), ~25px in the `text-sm` Analytics
     *   tables (Aura: ~28px);
     * - icon size: no override needed, Aura's `icon.size` is already 0.75rem;
     * - radius: kept, {content.border.radius} (6px), `rounded` = {border.radius.xl}.
     * Colours keep the Aura severities. Colour a Tag with `severity`, never with
     * Tailwind colour classes or `!`.
     *
     * STRUCTURE NOTE (@primeuix/themes v2.0.3): Aura keeps the size tokens at the
     * root of the block, outside colorScheme, so they apply to both schemes:
     * tag.root.fontSize -> --p-tag-font-size,
     * tag.root.fontWeight -> --p-tag-font-weight.
     * definePreset deep-merges `root`, so Aura's padding, gap and radii stay.
     */
    tag: {
      root: {
        fontSize: '0.75rem',
        fontWeight: '600'
      }
    },

    /**
     * Badge customization (counters, e.g. the notification bell's unread count)
     *
     * Aura's dark danger Badge is a light {red.400} (#f87171) fill with {red.950}
     * text. The former DIY bell counter was red-600 with white text, and the
     * danger Badge goes back to it: {red.600} (#dc2626) with white text, 4.8:1,
     * the same red as the error banner border (see the message block).
     *
     * OverlayBadge (NotificationBell.vue) renders a plain Badge (`.p-badge`
     * inside `.p-overlaybadge`), so these Badge tokens colour it with no `pt` or
     * class: `<OverlayBadge severity="danger">` picks them up. The same goes for
     * any `<Badge severity="danger">`. Its 2px outline is the OverlayBadge's own
     * token ({content.background}) and is kept. Other severities, sizes and the
     * radius keep the Aura defaults.
     *
     * STRUCTURE NOTE (@primeuix/themes v2.0.3): as in Aura, the severity colours
     * sit directly under colorScheme > dark, with no `root` level:
     * badge.colorScheme.dark.danger.background -> --p-badge-danger-background,
     * badge.colorScheme.dark.danger.color -> --p-badge-danger-color.
     * The light scheme keeps the Aura defaults (the app is dark-only).
     */
    badge: {
      colorScheme: {
        dark: {
          danger: {
            background: '{red.600}',
            color: '#ffffff'
          }
        }
      }
    },

    /**
     * Divider component customization
     *
     * Colour: no override needed. Aura maps `divider.border.color` to
     * {content.border.color}, which is {surface.700} in the dark scheme, i.e.
     * {gray.700} (#374151) here: the same 1px line as the old DIY Separator
     * (`var(--p-surface-700)`). The default horizontal margin (Aura `1rem 0`) is
     * left untouched, so a plain `<Divider />` keeps it.
     *
     * Spacing variants for the horizontal Divider. PrimeVue CSS is unlayered
     * (`cssLayer: false` in main.js), so Tailwind margin utilities on `<Divider>`
     * lose to `.p-divider-horizontal { margin }`. These selectors add a second
     * class and win on specificity, with no `!` override:
     * - `divider-flush`: no margin, for lines inside flex/gap layouts.
     *     <Divider class="divider-flush" />
     * - `divider-below`: no top margin, 1rem bottom margin.
     *     <Divider class="divider-below" />
     *
     * The margins are `extend` tokens (--p-divider-flush-margin,
     * --p-divider-below-margin). They sit under their own top-level keys on
     * purpose: `extend` is shallow-merged into the component tokens, so an
     * `extend.horizontal` key would replace Aura's whole `horizontal` group
     * (default margin and padding included).
     */
    divider: {
      extend: {
        flush: {
          margin: '0'
        },
        below: {
          margin: '0 0 1rem 0'
        }
      },
      css: ({ dt }) => `
        .p-divider-horizontal.divider-flush {
            margin: ${dt('divider.flush.margin')};
        }

        .p-divider-horizontal.divider-below {
            margin: ${dt('divider.below.margin')};
        }
      `
    }
  }
});

export default Crafter;
